/**
 * IPL Math Utilities
 *
 * Pure functions for coordinate transforms, zoom calculations, and
 * pan boundary computations. All functions are stateless and testable.
 */

/**
 * Coordinate pair
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Rectangle dimensions
 */
export interface Rect {
  width: number;
  height: number;
}

/**
 * Transform state
 */
export interface Transform {
  scale: number;
  translateX: number;
  translateY: number;
}

/**
 * Object-fit scale result
 */
export interface ObjectFitResult {
  scale: number;
  actualWidth: number;
  actualHeight: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Compute object-fit: contain scaling for an image
 *
 * Given natural image dimensions and displayed element dimensions,
 * calculates how the image is actually displayed with object-fit: contain.
 *
 * @param naturalSize Natural image dimensions
 * @param displayedSize Displayed element dimensions
 * @returns Object-fit scaling information
 */
export function computeObjectFitContain(
  naturalSize: Rect,
  displayedSize: Rect
): ObjectFitResult {
  const scaleX = displayedSize.width / naturalSize.width;
  const scaleY = displayedSize.height / naturalSize.height;

  // object-fit: contain uses the smaller scale
  const scale = Math.min(scaleX, scaleY);

  // Calculate actual displayed image dimensions
  const actualWidth = naturalSize.width * scale;
  const actualHeight = naturalSize.height * scale;

  // Calculate offset for centering (object-fit: contain centers the image)
  const offsetX = (displayedSize.width - actualWidth) / 2;
  const offsetY = (displayedSize.height - actualHeight) / 2;

  return { scale, actualWidth, actualHeight, offsetX, offsetY };
}

/**
 * The coordinate format of one drawing's `data-finden` buttons.
 *
 * - `fraction` — [0,1] of the drawing frame. Emitted by the `spare_parts_platform`
 *   renderer (MarkerView), normalised at the scraper.
 * - `pixel` — natural pixels of the served image. The OLD scraper generator's
 *   format, still stored in production's category descriptions and product
 *   attributes (586 of 590 IPL descriptions on the 2026-10-02 production clone).
 *   Production renders that stored HTML until go-live and again after a
 *   rollback, so the theme must place both.
 */
export type CoordinateSpace = 'fraction' | 'pixel';

/**
 * Decide the coordinate format for ONE drawing from all of its buttons.
 *
 * Any value above 1 can only be a pixel, so it marks the whole drawing as
 * `pixel`. The decision is per drawing on purpose: a pixel button near the
 * top-left corner, e.g. (0, 1), would read as a fraction on its own.
 */
export function detectCoordinateSpace(points: readonly Point[]): CoordinateSpace {
  return points.some((point) => point.x > 1 || point.y > 1) ? 'pixel' : 'fraction';
}

/**
 * Compute scaled coordinates for a button positioned on an image
 *
 * Converts a button's coordinates to percentages of the displayed element,
 * accounting for object-fit: contain scaling and centering (letterbox offsets).
 *
 * - `fraction`: the fraction maps onto the actual displayed image area —
 *   `fraction * actualWidth + offsetX`.
 * - `pixel`: production's formula for the stored HTML, unchanged —
 *   `pixel * objectFit.scale + offsetX`. It assumes the pixels are in the served
 *   image's natural size, which is how the old generator wrote them.
 *
 * @param coords Button coordinates in `space`
 * @param space Coordinate format of this button's drawing ({@link detectCoordinateSpace})
 * @param objectFit Object-fit scaling information
 * @param displayedSize Displayed element dimensions
 * @returns Coordinates as percentages of the displayed element
 */
export function computeScaledCoordinates(
  coords: Point,
  space: CoordinateSpace,
  objectFit: ObjectFitResult,
  displayedSize: Rect
): Point {
  const scaledX = space === 'fraction'
    ? coords.x * objectFit.actualWidth + objectFit.offsetX
    : coords.x * objectFit.scale + objectFit.offsetX;
  const scaledY = space === 'fraction'
    ? coords.y * objectFit.actualHeight + objectFit.offsetY
    : coords.y * objectFit.scale + objectFit.offsetY;

  // Convert to percentages of the image element
  const x = (scaledX / displayedSize.width) * 100;
  const y = (scaledY / displayedSize.height) * 100;

  return { x, y };
}

/**
 * Compute inverse scale for button sizing
 *
 * Buttons should maintain a constant visual size regardless of zoom level.
 * This computes the inverse scale to apply to buttons.
 *
 * @param zoom Current zoom scale
 * @param minInverseScale Minimum inverse scale (prevents buttons becoming too large)
 * @returns Inverse scale to apply to buttons
 */
export function computeInverseScale(zoom: number, minInverseScale = 0.3): number {
  return Math.max(minInverseScale, 1 / zoom);
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Compute pan boundaries for a zoomed/scaled element
 *
 * Given container and content dimensions, calculates the valid range
 * for pan translation to keep content visible.
 *
 * @param containerSize Container dimensions
 * @param contentSize Content dimensions (before scaling)
 * @param scale Current scale factor
 * @returns Valid translation range { minX, maxX, minY, maxY }
 */
export function computePanBoundaries(
  containerSize: Rect,
  contentSize: Rect,
  scale: number
): { minX: number; maxX: number; minY: number; maxY: number } {
  const scaledWidth = contentSize.width * scale;
  const scaledHeight = contentSize.height * scale;

  let minX: number, maxX: number, minY: number, maxY: number;

  // X boundaries
  if (scaledWidth > containerSize.width) {
    // Content is wider than container - allow panning
    minX = containerSize.width - scaledWidth;
    maxX = 0;
  } else {
    // Content fits within container - center it
    const centerX = (containerSize.width - scaledWidth) / 2;
    minX = maxX = centerX;
  }

  // Y boundaries
  if (scaledHeight > containerSize.height) {
    // Content is taller than container - allow panning
    minY = containerSize.height - scaledHeight;
    maxY = 0;
  } else {
    // Content fits within container - center it
    const centerY = (containerSize.height - scaledHeight) / 2;
    minY = maxY = centerY;
  }

  return { minX, maxX, minY, maxY };
}

/**
 * Clamp transform to valid pan boundaries
 *
 * @param transform Current transform state
 * @param containerSize Container dimensions
 * @param contentSize Content dimensions
 * @returns Clamped transform
 */
export function clampTransform(
  transform: Transform,
  containerSize: Rect,
  contentSize: Rect
): Transform {
  const boundaries = computePanBoundaries(containerSize, contentSize, transform.scale);

  return {
    scale: transform.scale,
    translateX: clamp(transform.translateX, boundaries.minX, boundaries.maxX),
    translateY: clamp(transform.translateY, boundaries.minY, boundaries.maxY),
  };
}

/**
 * Check if content can be panned (is larger than container)
 *
 * @param containerSize Container dimensions
 * @param contentSize Content dimensions
 * @param scale Current scale factor
 * @returns true if content can be panned
 */
export function canPan(
  containerSize: Rect,
  contentSize: Rect,
  scale: number
): boolean {
  const scaledWidth = contentSize.width * scale;
  const scaledHeight = contentSize.height * scale;

  return scaledWidth > containerSize.width || scaledHeight > containerSize.height;
}
